/**
 * First-party measurement of the sign-in and Setup screens ONLY (docs/SITE-ANALYTICS.md; core siteanalytics.ts).
 *  - captureAttribution: the site's sign-up link carries how the reader came (?src=google&st=UT&utm=news, a bucket, never an id).
 *    Kept for this tab only (sessionStorage), so the sign-in and the business setup can say it; the server counts, it never stores
 *    it against a person.
 *  - usePageMeasure(page): a page view, the deepest scroll and clicks as positions, sent once when the screen closes or the tab
 *    hides. It measures only a page the core allowlist lets in ("app:sign-in", "app:setup/<step>"): a screen after onboarding is
 *    never measured, whatever calls this. Nothing at all under Do Not Track or Global Privacy Control, and never in the demo.
 */
import { useEffect } from "react";
import { cleanState, cleanUtm, cleanVisitSource, pageAllowed, viewportOf } from "@logwave/core";
import { config, IS_MOCK, MOCK_BUILD } from "../env.ts";

export const SRC_KEY = "cc.src";
export interface Attribution { src?: string; st?: string; utm?: string }

export function optedOut(win: Window = window): boolean {
  const n = win.navigator as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string };
  return n.doNotTrack === "1" || n.doNotTrack === "yes" || (win as Window & { doNotTrack?: string }).doNotTrack === "1" || n.msDoNotTrack === "1" || n.globalPrivacyControl === true;
}
const store = (win: Window): Storage | undefined => { try { return win.sessionStorage; } catch { return undefined; } };

/** Keeps the link's bucket for this tab. Returns what it kept, or null (none, junk, or the reader opted out). */
export function captureAttribution(search: string, win: Window = window): Attribution | null {
  if (optedOut(win)) return null;
  const p = new URLSearchParams(search);
  // Every site button's link already carries its PAGE as `src` (core buyintent.ts: src=pricing, src=blog-…), and the site then adds
  // the visit's bucket as a second `src` (site signup.js + pv.js). Reading only the first one dropped the bucket on every sign-up
  // (docs/marketing/CONVERSION-2026-10-13.md C1), so the bucket is the first `src` that is one.
  const src = p.getAll("src").map(cleanVisitSource).find((v) => v !== "none") ?? "none";
  if (src === "none") return null;
  const a: Attribution = { src, ...(cleanState(p.get("st")) ? { st: cleanState(p.get("st")) } : {}), ...(cleanUtm(p.get("utm")) ? { utm: cleanUtm(p.get("utm")) } : {}) };
  try { store(win)?.setItem(SRC_KEY, JSON.stringify(a)); } catch { /* blocked: the sign-up just counts as "not through the site" */ }
  return a;
}

/** What the sign-in and the business setup send along ({} when none, or opted out). */
export function attribution(win: Window = window): Attribution {
  if (optedOut(win)) return {};
  try {
    const a = JSON.parse(store(win)?.getItem(SRC_KEY) ?? "null") as Attribution | null;
    if (!a || cleanVisitSource(a.src) === "none") return {};
    return { src: a.src, ...(cleanState(a.st) ? { st: cleanState(a.st) } : {}), ...(cleanUtm(a.utm) ? { utm: cleanUtm(a.utm) } : {}) };
  } catch { return {}; }
}

const TARGET = /^(?:cc:[a-z0-9][a-z0-9-]{0,39}|#[A-Za-z][A-Za-z0-9_-]{0,40}|signup|signin|a|button|summary|label|img)$/;
/** The clicked element's stable name (a data-testid or data-cc name, an id, or the kind of control): never its text or value. */
export function targetOf(el: EventTarget | null): string | null {
  if (!el || typeof (el as Element).closest !== "function") return null;
  const e = el as Element;
  const named = e.closest("[data-cc],[data-testid]");
  if (named) { const v = `cc:${named.getAttribute("data-cc") ?? named.getAttribute("data-testid")}`; if (TARGET.test(v)) return v; }
  const withId = e.closest("[id]");
  if (withId && TARGET.test(`#${withId.id}`)) return `#${withId.id}`;
  const kind = e.closest("a,button,summary,label,img");
  return kind ? kind.tagName.toLowerCase() : null;
}

export type Send = (url: string, body: string) => void;
/**
 * The browser's own sender exists only in a REAL build (vite.config.ts sets __CC_MOCK__ false), never under Bun's tests or the dev
 * server (MOCK_BUILD true there). Decided by the build, not the user agent: a test that sets navigator.userAgent (push.test.tsx does)
 * once let happy-dom's sendBeacon, a real fetch to 127.0.0.1:8787, run on every Setup step and the web suite spun for hours.
 */
export const realBrowser = (win: Window, mockBuild = MOCK_BUILD) => !mockBuild && typeof win.navigator.sendBeacon === "function" && /^https?:$/.test(win.location.protocol);

/** Starts measuring one screen; returns the stop (which sends). Null when this page may not be measured or nothing may be sent. */
export function startMeasure(page: string, win: Window = window, send?: Send): (() => void) | null {
  if (IS_MOCK && !send) return null;
  if (!pageAllowed(page) || optedOut(win)) return null;
  // The browser's own sender only in a real browser: the test DOM's sendBeacon is a real fetch to the API address (it hung the
  // Setup tests by posting to 127.0.0.1:8787 on every step). Tests pass `send`.
  const beacon: Send | null = send ?? (realBrowser(win) ? (u, b) => { win.navigator.sendBeacon(u, b); } : null);
  if (!beacon) return null;
  const doc = win.document;
  const vp = viewportOf(win.innerWidth || 1024);
  let maxScroll = 0, clicks: (number | string)[][] = [], viewSent = false;
  const onScroll = () => {
    const h = Math.max(doc.documentElement.scrollHeight || 0, 1);
    maxScroll = Math.max(maxScroll, Math.min(100, Math.floor((((win.scrollY || 0) + (win.innerHeight || 0)) / h) * 10) * 10));
  };
  const onClick = (ev: MouseEvent) => {
    if (clicks.length >= 60) return;
    const width = Math.max(doc.documentElement.scrollWidth || 0, win.innerWidth || 0, 1);
    const x = Math.max(0, Math.min(100, Math.round((((ev.clientX || 0) + (win.scrollX || 0)) / width) * 100)));
    const y = Math.max(0, Math.min(20_000, Math.floor(((ev.clientY || 0) + (win.scrollY || 0)) / 50) * 50));
    const t = targetOf(ev.target);
    clicks.push(t ? [x, y, t] : [x, y]);
  };
  const flush = () => {
    if (viewSent && !clicks.length) return;
    const b: Record<string, unknown> = { p: page, vp };
    if (!viewSent) { b.v = true; b.sc = maxScroll; }
    if (clicks.length) b.c = clicks;
    viewSent = true; clicks = [];
    try { beacon(`${config.apiUrl}/pv`, JSON.stringify(b)); } catch { /* never in the person's way */ }
  };
  const onHide = () => { if (doc.visibilityState === "hidden") flush(); };
  onScroll();
  win.addEventListener("scroll", onScroll, { passive: true });
  doc.addEventListener("click", onClick, { capture: true, passive: true });
  win.addEventListener("pagehide", flush);
  doc.addEventListener("visibilitychange", onHide);
  return () => {
    win.removeEventListener("scroll", onScroll); doc.removeEventListener("click", onClick, { capture: true });
    win.removeEventListener("pagehide", flush); doc.removeEventListener("visibilitychange", onHide);
    flush();
  };
}

/** Measures this screen while it is shown (sign-in and Setup only; anything else is refused by the allowlist). */
export function usePageMeasure(page: string): void {
  useEffect(() => startMeasure(page) ?? undefined, [page]);
}
